import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const getEduLabel = (value) => {
  switch (value) {
    case "E0":
      return "All Education Categories";
    case "E1":
      return "Less than high school";
    case "E2":
      return "High school or equivalent";
    case "E3":
      return "Some college / Associate degree";
    case "E4":
      return "Bachelor's or advanced degree";
    case "E5":
      return "Educational attainment not available";
    default:
      return "Unknown";
  }
};

export default function EducationTotalsStackedChart({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p className="text-center">No data available</p>;
  }

  /* =============================
     DYNAMIC YEAR EXTRACTION
  ============================== */

  const years = Object.keys(data[0])
    .filter((key) => key.startsWith("total_"))
    .map((key) => key.replace("total_", ""))
    .sort();

  /* =============================
     SUMMARY CALCULATIONS
  ============================== */

  const latestYear = years[years.length - 1];

  const latestTotals = data.map((row) => ({
    label: getEduLabel(row.education_label),
    value: Number(row[`total_${latestYear}`] || 0),
  }));

  const highest = latestTotals.reduce((a, b) => (b.value > a.value ? b : a));

  const lowest = latestTotals.reduce((a, b) => (b.value < a.value ? b : a));

  const totalWorkforceLatest = latestTotals.reduce(
    (sum, row) => sum + row.value,
    0,
  );

  /* =============================
     CHART DATA
  ============================== */

  const colorPalette = [
    "#0ea5e9",
    "#ec4899",
    "#10b981",
    "#f59e0b",
    "#6366f1",
    "#ef4444",
  ];

  const chartData = {
    labels: years,
    datasets: data.map((row, index) => ({
      label: getEduLabel(row.education_label),
      data: years.map((year) => Number(row[`total_${year}`] || 0)),
      backgroundColor: colorPalette[index % colorPalette.length],
      borderWidth: 1,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Education Totals by Year (Stacked View)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toLocaleString("en-US")}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: { display: true, text: "Total Employment" },
        ticks: {
          callback: (value) => value.toLocaleString("en-US"),
        },
      },
    },
  };

  /* =============================
     MULTI-PAGE PDF EXPORT
  ============================== */

    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text("20250916.v3.198", 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );

        pdf.save("education_totals_stacked.pdf");
      });
    };

  /* =============================
     UI
  ============================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Education Totals by Year (Stacked)
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Latest Year</p>
            <p className="text-xl font-bold text-blue-800">{latestYear}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Category
            </p>
            <p className="text-md font-bold text-green-800">{highest.label}</p>
          </div>

          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Lowest Category</p>
            <p className="text-md font-bold text-red-800">{lowest.label}</p>
          </div>

          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Workforce ({latestYear})
            </p>
            <p className="text-lg font-bold text-purple-800">
              {totalWorkforceLatest.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Education</th>
                  {years.map((year) => (
                    <th key={year} className="p-2">
                      {year}
                    </th>
                  ))}
                  <th className="p-2">Average</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-100">
                    <td className="p-2 font-medium">
                      {getEduLabel(row.education_label)}
                    </td>
                    {years.map((year) => (
                      <td key={year} className="p-2">
                        {Number(row[`total_${year}`] || 0).toLocaleString(
                          "en-US",
                        )}
                      </td>
                    ))}
                    <td className="p-2 font-semibold text-blue-700">
                      {Number(row.average || 0).toLocaleString("en-US")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
